import { io, type Socket } from "socket.io-client";
import { buildActiveRoleAuth, buildActiveRoleSocketOptions } from "@/api/active-role-header";
import { EnvVariables } from "@/env-variables";
import LocalStorage from "@/storages/LocalStorage";
import { useAdditionalInfo } from "@/stores/domains/additionalInfo/useAdditionalInfo";
import {
  type ForceLogoutData,
  SessionClientEvents,
  type SessionExpiringData,
  SessionServerEvents,
} from "@/types/collaborative";

// Event callback type
type EventCallback = (data: unknown) => void;

/**
 * Session Socket Service
 * Manages socket connection for user session management
 * Handles session expiring warnings (14 min) and force logout (15 min)
 */
class SessionSocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private isConnecting = false;
  private listeners: Map<string, EventCallback[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private currentSessionId: string | null = null;
  private isSessionRegistered = false;

  /**
   * Connect to session socket namespace
   */
  public connect(): void {
    if (this.isConnected || this.isConnecting) {
      return;
    }

    if (this.socket) {
      this.disconnect();
    }

    this.isConnecting = true;

    try {
      const accessToken = LocalStorage.getAccessToken();

      const sessionWebsocketUrl = EnvVariables.sessionWebsocketUrl || EnvVariables.websocketUrl;

      // Check if websocket URL is configured
      if (!sessionWebsocketUrl) {
        console.warn("WebSocket URL not configured. Session socket will not be established.");
        this.isConnecting = false;
        return;
      }

      // `/session` is a Socket.IO NAMESPACE, not the engine.io `path`. It must be
      // appended to the connection URL; the transport path stays the default
      // `/socket.io` (matches the auth-service `new Server()` config).
      this.socket = io(`${sessionWebsocketUrl}/session`, {
        // `auth` carries the active role as well; `extraHeaders` is sent for parity with HTTP
        // but only reaches a polling transport, which this namespace does not use.
        ...buildActiveRoleSocketOptions(
          accessToken ? { token: accessToken } : {},
          useAdditionalInfo.getState().role,
        ),
        // WebSocket-only: the auth-service reads the JWT from `handshake.auth.token`
        // (verify-jwt.ts), NOT from an Authorization header — so polling buys us
        // nothing. Polling is also an XHR that trips the socket CORS preflight
        // (server origin is locked to API_GATEWAY_URL), which blocks localhost dev.
        // The WS upgrade is not subject to that preflight.
        transports: ["websocket"],
        reconnectionAttempts: 3,
        reconnectionDelay: 1000,
        timeout: 20000,
        withCredentials: false,
        autoConnect: true,
      });

      this.setupSocketListeners();
    } catch (error) {
      console.error("SessionSocketService - Error creating Socket.IO instance:", error);
      this.isConnecting = false;
      this.emit("error", { error: `Failed to create Socket.IO instance: ${error}` });
    }
  }

  /**
   * Setup socket event listeners
   */
  private setupSocketListeners(): void {
    if (!this.socket) return;

    // Basic connection events
    this.socket.on("connect", () => {
      this.isConnected = true;
      this.isConnecting = false;
      this.reconnectAttempts = 0;

      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      this.emit("connect", {});

      // Auto-register session if we have a session ID and haven't registered yet
      if (this.currentSessionId && !this.isSessionRegistered) {
        this.registerSession(this.currentSessionId);
      }
    });

    this.socket.on("disconnect", (reason) => {
      this.isConnected = false;
      this.isConnecting = false;
      this.isSessionRegistered = false;
      this.emit("disconnect", { reason });

      // Attempt to reconnect if disconnection was not intentional
      if (reason !== "io client disconnect" && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      }
    });

    this.socket.on("connect_error", (error) => {
      console.error("Session socket connection error:", error);
      this.isConnecting = false;
      this.emit("error", { error: `Connection error: ${error.message}` });
    });

    // Setup session event listeners
    this.setupSessionEventListeners();
  }

  /**
   * Setup session specific event listeners
   */
  private setupSessionEventListeners(): void {
    if (!this.socket) return;

    // Session registered confirmation
    this.socket.on(SessionServerEvents.SessionRegistered, (data: unknown) => {
      this.emit("sessionRegistered", data);
    });

    // Session expiring warning (14 minutes of inactivity)
    this.socket.on(SessionServerEvents.SessionExpiring, (data: SessionExpiringData) => {
      this.emit("sessionExpiring", data);
    });

    // Force logout (15 minutes of inactivity)
    this.socket.on(SessionServerEvents.ForceLogout, (data: ForceLogoutData) => {
      this.emit("forceLogout", data);
    });

    // Session errors
    this.socket.on(SessionServerEvents.SessionError, (data: unknown) => {
      this.emit("sessionError", data);
    });
  }

  /**
   * Register a session with the socket server
   * Should be called after obtaining session ID from API
   * @param sessionId - The session ID obtained from session register API
   */
  public registerSession(sessionId: string): void {
    if (!this.socket || !this.isConnected) {
      // Store session ID for auto-registration when connected
      this.currentSessionId = sessionId;
      this.isSessionRegistered = false;
      return;
    }

    this.currentSessionId = sessionId;
    this.isSessionRegistered = true;
    this.socket.emit(SessionClientEvents.RegisterSession, sessionId);
  }

  /**
   * Send keep-alive ping to maintain session
   * This could be used to manually trigger a keep-alive if needed
   */
  public sendKeepAlive(): void {
    if (!this.socket || !this.isConnected) {
      return;
    }

    if (!this.currentSessionId) {
      console.warn("SessionSocketService: No session ID to send keep-alive");
      return;
    }

    this.socket.emit(SessionClientEvents.KeepAlive, this.currentSessionId);
  }

  /**
   * Clear current session ID
   * Should be called on logout
   */
  public clearSession(): void {
    this.currentSessionId = null;
    this.isSessionRegistered = false;
  }

  /**
   * Disconnect from socket
   */
  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnected = false;
    this.isConnecting = false;
    this.currentSessionId = null;
  }

  /**
   * Subscribe to an event
   * @param event - Event name
   * @param callback - Callback function
   */
  public on(event: string, callback: EventCallback): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)?.push(callback);
  }

  /**
   * Unsubscribe from an event
   * @param event - Event name
   * @param callback - Callback function to remove
   */
  public off(event: string, callback: EventCallback): void {
    if (!this.listeners.has(event)) return;

    const callbacks = this.listeners.get(event) || [];
    const index = callbacks.indexOf(callback);

    if (index !== -1) {
      callbacks.splice(index, 1);
    }

    if (callbacks.length === 0) {
      this.listeners.delete(event);
    }
  }

  /**
   * Emit an event to all subscribers
   * @param event - Event name
   * @param data - Event data
   */
  private emit(event: string, data: unknown): void {
    const callbacks = this.listeners.get(event) || [];
    callbacks.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in "${event}" event handler:`, error);
      }
    });
  }

  /**
   * Check if socket is connected
   */
  public isSocketConnected(): boolean {
    return this.isConnected && this.socket !== null;
  }

  /**
   * Get current session ID
   */
  public getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * 2 ** (this.reconnectAttempts - 1), 10000);

    console.log(
      `SessionSocketService: Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`,
    );

    this.reconnectTimeout = setTimeout(() => {
      if (this.socket) {
        // The socket is created once, so refresh the handshake before reconnecting: the token
        // may have been rotated and the active role may have changed since it was built.
        this.socket.auth = buildActiveRoleAuth(
          { token: LocalStorage.getAccessToken() },
          useAdditionalInfo.getState().role,
        );
        this.socket.connect();
      }
    }, delay);
  }

  /**
   * Remove all event listeners
   */
  public removeAllListeners(): void {
    this.listeners.clear();
    if (this.socket) {
      Object.values(SessionServerEvents).forEach((event) => {
        this.socket?.removeAllListeners(event);
      });
    }
  }
}

// Export singleton instance
const sessionSocketService = new SessionSocketService();
export default sessionSocketService;
